import { Chart, ChartData, ChartDataset, ChartOptions, Point } from "chart.js";
import {
    _DeepPartialArray,
    _DeepPartialObject,
} from "chart.js/dist/types/utils";
import {
    AnnotationOptions,
    AnnotationTypeRegistry,
} from "chartjs-plugin-annotation";
import { useCallback, useMemo } from "react";
import {
    ConfigKey,
    DEFAULT_LINE1_COLOR_ANAEROBIC,
    DEFAULT_LINE1_COLOR_WASSERMAN,
    DEFAULT_LINE2_COLOR_ANAEROBIC,
    DEFAULT_LINE2_COLOR_WASSERMAN,
    DEFAULT_LINE3_COLOR_ANAEROBIC,
    DEFAULT_LINE3_COLOR_WASSERMAN,
    FontWeight,
    graphConfig,
} from "../components/interpretation/Graph/config";
import { TestGraph } from "../types";
import { measureTextWidth } from "../utils";

type Response = "1" | "2" | "3";
type ResponseKey = `response${Response}_xy`;

const mapResponseOrderToYAxisId = {
    1: "y1",
    2: "y2",
    3: "y3",
};

const orderLevels = {
    trendLine: 0,    // On top of everything
    peak: 1,         // Below trend lines
    dataLine: 2,     // Below peaks
    historicLine: 2, // Same level as data lines
    areaGraph: 3,    // Behind everything
} as const;

const getLineColor = (
    response: "1" | "2" | "3",
    isAnaerobic: boolean
): string => {
    if (response === "1") {
        return isAnaerobic
            ? DEFAULT_LINE1_COLOR_ANAEROBIC
            : DEFAULT_LINE1_COLOR_WASSERMAN;
    } else if (response === "2") {
        return isAnaerobic
            ? DEFAULT_LINE2_COLOR_ANAEROBIC
            : DEFAULT_LINE2_COLOR_WASSERMAN;
    } else {
        return isAnaerobic
            ? DEFAULT_LINE3_COLOR_ANAEROBIC
            : DEFAULT_LINE3_COLOR_WASSERMAN;
    }
};

export default function useGraphConfig({
    graphData,
    isAnaerobic,
    isWasserman9,
    showGraph1PointPopup,
    showGraph2PointPopup,
    showGraph3PointPopup,
    size,
    onGraphClick,
    vat,
    rcp,
    isReverseVATColor = false,
    disablePeakHover = false,
    disableLineHover = false,
}: {
    graphData: TestGraph;
    isAnaerobic: boolean;
    isWasserman9: boolean;
    showGraph1PointPopup: boolean;
    showGraph2PointPopup: boolean;
    showGraph3PointPopup: boolean;
    onGraphClick: (e: any, elements: any, datasets: any[]) => void;
    vat: number | undefined;
    rcp: number | undefined;
    size:
        | "sm"
        | "md"
        | "lg"
        | "xl"
        | "compare-primary"
        | "compare-secondary"
        | "xxl";
    isReverseVATColor?: boolean;
    disablePeakHover?: boolean;
    disableLineHover?: boolean;
}) {
    graphData["x"] = (
        graphData.response1_xy ? graphData.response1_xy : graphData.response2_xy
    )?.map(({ x }) => x);

    const hasSecondGraph = Boolean(graphData.response2_y_label);
    const hasThirdGraph = Boolean(graphData.response3_y_label);

    const lowerLimit = graphData.slope_calculation_begin_value;
    const upperLimit = graphData.slope_calculation_end_value;

    const isRCP =
        graphData.rcp_value !== null && graphData.rcp_value !== undefined;

    const withOpacity = showGraph1PointPopup || showGraph2PointPopup;

    const findPointIndex = (
        Ys: number[] | undefined,
        point: { x: number; y: number } | undefined
    ): number => {
        if (!Ys || !point) return -1;
        for (let i = 0; i < Ys.length; i++) {
            if (Ys[i] === point.y && graphData.x?.[i] === point.x) {
                return i;
            }
        }
        return -1;
    };

    const peak1PointIndex = findPointIndex(
        graphData.response1_xy?.map(({ y }) => y),
        graphData.peak1_measured
    );

    const peak2PointIndex = findPointIndex(
        graphData.response2_xy?.map(({ y }) => y),
        graphData.peak2_measured
    );

    const peak3PointIndex = findPointIndex(
        graphData.response3_xy?.map(({ y }) => y),
        graphData.peak3_measured
    );

    const generateLineData = useCallback(
        ({ response }: { response: Response }): ChartGraphData => {
            const responseKey: ResponseKey = `response${response}_xy`;
            const responseType = graphData[`slope${response}_status_color`]
                ? "slope"
                : "response";
            const responseColorsArray =
                graphData[
                    `${responseType}${response as "1" | "2"}_other_statuses`
                ];
            const responseStatusColor =
                responseColorsArray?.find(
                    (item) =>
                        item.status ===
                        graphData[
                            `${responseType}${response as "1" | "2"}_status`
                        ]
                )?.status_color ||
                graphData[
                    `${responseType}${response as "1" | "2"}_status_color`
                ];

            const DEFAULT_LINE_COLOR = getLineColor(response, isAnaerobic);
            const dotSize = size === "xxl" ? 3 : 1.5;

            const lineColor = responseStatusColor ?? DEFAULT_LINE_COLOR;

            return {
                label: `response ${response}`,
                data: graphData[responseKey] ?? [],
                borderColor: lineColor,
                backgroundColor: lineColor,
                pointBorderColor: lineColor,
                pointBackgroundColor: lineColor,
                fill: false,
                pointStyle: "circle",
                pointRadius: dotSize,
                pointHitRadius: dotSize * 1.25,
                pointHoverRadius: !disableLineHover
                    ? Math.max(dotSize * 2, dotSize + 3)
                    : dotSize + 1,
                borderWidth: size === "xxl" ? 5 : 3,
                yAxisID: mapResponseOrderToYAxisId[response],
                order: orderLevels.dataLine,
            };
        },
        [
            graphData,
            graphData.response1_status,
            graphData.response1_other_statuses,
            graphData.response2_status,
            graphData.response2_other_statuses,
            graphData.slope1_status,
            graphData.slope1_other_statuses,
            graphData.slope2_status,
            graphData.slope2_other_statuses,
            graphData.slope3_status,
            graphData.slope3_other_statuses,
            isAnaerobic,
            size,
            disableLineHover,
        ]
    );

    const generatePeakData = useCallback(
        ({ response }: { response: Response }): ChartGraphData | null => {
            const responseKey: ResponseKey = `response${response}_xy`;
            const peakColorsArray =
                graphData[`peak${response}_other_statuses`] || [];
            const peakStatusColor =
                peakColorsArray.find(
                    (item) =>
                        item.status === graphData[`peak${response}_status`]
                )?.status_color || graphData[`peak${response}_status_color`];

            const peakPointIndex =
                response === "1"
                    ? peak1PointIndex
                    : response === "2"
                    ? peak2PointIndex
                    : peak3PointIndex;

            // If no peak point found, return null
            if (peakPointIndex === -1) return null;

            const peakDotSize = size === "xxl" ? 8 : 5;
            const peakPoint = graphData[responseKey]?.[peakPointIndex];

            if (!peakPoint) return null;

            return {
                label: `peak ${response}`,
                data: [peakPoint], // Single point dataset
                borderColor: peakStatusColor,
                backgroundColor: peakStatusColor,
                pointBorderColor: peakStatusColor,
                pointBackgroundColor: peakStatusColor,
                fill: false,
                // @ts-ignore
                zzz: 123,
                pointStyle: "circle",
                pointRadius: peakDotSize,
                pointHitRadius: peakDotSize * 1.25,
                pointHoverRadius: !disablePeakHover
                    ? peakDotSize * 1.5
                    : peakDotSize + 1,
                showLine: false, // Only show the point, no line
                yAxisID: mapResponseOrderToYAxisId[response],
                order: orderLevels.peak,
            };
        },
        [
            graphData,
            graphData.peak1_status,
            graphData.peak1_other_statuses,
            graphData.peak2_status,
            graphData.peak2_other_statuses,
            graphData.peak3_status,
            graphData.peak3_other_statuses,
            peak1PointIndex,
            peak2PointIndex,
            peak3PointIndex,
            size,
            disablePeakHover,
        ]
    );

    type ChartGraphData = ChartDataset<"scatter", (number | Point | null)[]>;

    const lineGraphs = useMemo<ChartGraphData[]>(() => {
        const graph1 = generateLineData({ response: "1" });
        const graph2 = generateLineData({ response: "2" });
        const graph3 = hasThirdGraph
            ? generateLineData({ response: "3" })
            : null;

        const peak1 = generatePeakData({ response: "1" });
        const peak2 = generatePeakData({ response: "2" });
        const peak3 = hasThirdGraph
            ? generatePeakData({ response: "3" })
            : null;

        const trend1: ChartGraphData = {
            label: "trend 1",
            data: graphData.trend1_xy ?? [],
            borderColor: "black",
            backgroundColor: "rgba(153, 102, 255, 0.2)",
            fill: false,
            pointStyle: false,
            borderWidth: size === "xxl" ? 2 : 1,
            yAxisID: "y1",
            order: orderLevels.trendLine,
        };
        const trend1b: ChartGraphData = {
            label: "trend 1b",
            data: graphData.trend1b_xy ?? [],
            borderColor: "black",
            backgroundColor: "rgba(153, 102, 255, 0.2)",
            fill: false,
            pointStyle: false,
            borderWidth: size === "xxl" ? 2 : 1,
            yAxisID: "y1",
            order: orderLevels.trendLine,
        };

        const trend2b: ChartGraphData = {
            label: "trend 2b",
            data: graphData.trend2b_xy ?? [],
            borderColor: "black",
            backgroundColor: "rgba(153, 102, 255, 0.2)",
            fill: false,
            pointStyle: false,
            borderWidth: size === "xxl" ? 2 : 1,
            yAxisID: "y2",
            order: orderLevels.trendLine,
        };

        const trend2: ChartGraphData = {
            label: "trend 2",
            data: graphData.trend2_xy ?? [],
            borderColor: "black",
            backgroundColor: "rgba(153, 102, 255, 0.2)",
            fill: false,
            pointStyle: false,
            borderWidth: size === "xxl" ? 2 : 1,
            yAxisID: "y2",
            order: orderLevels.trendLine,
        };

        const historicGraphs: ChartDataset<
            "scatter",
            (number | Point | null)[]
        >[] = [];

        Object.keys(graphData).forEach((key) => {
            const match = key.match(/^response(\d+)_xy_(\d+)$/);
            if (match) {
                const responseId = match[1];
                const testId = match[2];
                const graph: ChartGraphData = {
                    label: `response ${responseId} - ${testId}`,
                    data: graphData[key as any],
                    borderColor: graphData[`${key}_color` as any] ?? "#808080",
                    backgroundColor:
                        graphData[`${key}_color` as any] ?? "#808080",
                    fill: false,
                    pointStyle: "circle",
                    pointRadius: size === "xxl" ? 3 : 1.5,
                    borderWidth: size === "xxl" ? 5 : 3,
                    yAxisID: `y${responseId}`,
                    order: orderLevels.historicLine,
                };
                historicGraphs.push(graph);
            }
        });

        const graphs: ChartGraphData[] = [];
        for (const g of [
            trend1,
            trend1b,
            trend2b,
            trend2,
            graph1,
            peak1,
            graph2,
            peak2,
            graph3,
            peak3,
            ...historicGraphs,
        ]) {
            if (g) {
                graphs.push(g);
            }
        }

        return graphs; //! this affects TREND_1_DATASET_INDEX etc.
    }, [generateLineData, generatePeakData, graphData, hasThirdGraph, size]);

    const areaGraphs = useMemo<ChartGraphData[]>(() => {
        const norm_high_1: ChartGraphData = {
            label: "norm high 1",
            data: graphData.response1_norm_high_xy ?? [],
            backgroundColor: `rgba(221, 238, 254, ${
                withOpacity ? "0.5" : "1"
            })`,
            borderColor: "transparent",
            pointStyle: false,
            fill: "+1", // Fill to the next dataset
            yAxisID: "y1",
            order: orderLevels.areaGraph,
        };

        const norm_low_1: ChartGraphData = {
            label: "norm low 1",
            data: graphData.response1_norm_low_xy ?? [],
            borderColor: "transparent",
            pointStyle: false,
            fill: false, // Fill to the bottom of the chart
            yAxisID: "y1",
            order: orderLevels.areaGraph,
        };

        const norm_high_2: ChartGraphData = {
            label: "norm high 2",
            data: graphData.response2_norm_high_xy ?? [],
            backgroundColor: `rgba(221, 238, 254, ${
                withOpacity ? "0.5" : "1"
            })`,
            borderColor: "transparent",
            pointStyle: false,
            fill: "+1", // Fill to the next dataset
            yAxisID: "y2",
            order: orderLevels.areaGraph,
        };

        const norm_low_2: ChartGraphData = {
            label: "norm low 2",
            data: graphData.response2_norm_low_xy ?? [],
            borderColor: "transparent",
            pointStyle: false,
            fill: false, // Fill to the bottom of the chart
            yAxisID: "y2",
            order: orderLevels.areaGraph,
        };

        return [norm_high_1, norm_low_1, norm_high_2, norm_low_2];
    }, [
        graphData.response1_norm_high_xy,
        graphData.response1_norm_low_xy,
        graphData.response2_norm_high_xy,
        graphData.response2_norm_low_xy,
        withOpacity,
    ]);

    const labelXValueSize =
        size === "xxl" || size === "compare-primary" ? "xxl" : "default";

    const additionalLabelFontSize = size === "xxl" ? 20 : 10;

    // MARK: Annotations (lines and boxes)
    const graph1Annotations: Annotation = useMemo((): Annotation => {
        const _annotations: Annotation = {
            pred_max1_line1: graphData.peak1_predicted
                ? {
                      yScaleID: "y1",
                      type: "line",
                      display: !showGraph1PointPopup,
                      yMin: graphData.peak1_predicted,
                      yMax: graphData.peak1_predicted,
                      xMax: graphData.peak1_predicted_label_start_x_value?.[
                          labelXValueSize
                      ],
                      borderDash: [2.5],
                      borderColor: "black",
                      borderWidth: 1,
                      drawTime: "beforeDraw",
                  }
                : undefined,
            pred_max1_line2: graphData.peak1_predicted
                ? {
                      yScaleID: "y1",
                      type: "line",
                      display: !showGraph1PointPopup,
                      yMin: graphData.peak1_predicted,
                      yMax: graphData.peak1_predicted,
                      xMin: graphData.peak1_predicted_label_end_x_value?.[
                          labelXValueSize
                      ],
                      borderDash: [2.5],
                      borderColor: "black",
                      borderWidth: 1,
                  }
                : undefined,
            pred_max_box1: {
                display: showGraph1PointPopup,
                yScaleID: "y1",
                type: "box",
                yMin: graphData.peak1_predicted_low,
                yMax: graphData.peak1_predicted_high,
                xMin: graphData.x?.[1],
                backgroundColor: "#EFEFEF",
                borderWidth: 0,
                z: 50,
                drawTime: "beforeDraw",
            },
            pred_max_box_upper_border1: {
                display: showGraph1PointPopup,
                yScaleID: "y1",
                type: "line",
                yMin: graphData.peak1_predicted_high,
                yMax: graphData.peak1_predicted_high,
                xMin: graphData.x?.[1],
                borderWidth: 1,
                borderDash: [2],
                z: 6,
            },
            pred_max_box_lower_border1: {
                display: showGraph1PointPopup,
                yScaleID: "y1",
                type: "line",
                yMin: graphData.peak1_predicted_low,
                yMax: graphData.peak1_predicted_low,
                xMin: graphData.x?.[1],
                borderWidth: 1,
                borderDash: [2],
                z: 6,
            },
        };
        const additionalAnnotationKeys = Object.keys(graphData).filter((key) =>
            key.match(/^horizontal(\d+)$/)
        ) as Array<`horizontal${number}`>;
        additionalAnnotationKeys.forEach((key) => {
            const horizontalLine = graphData[key];
            if (horizontalLine) {
                const labelWidth =
                    measureTextWidth(
                        horizontalLine.label,
                        additionalLabelFontSize
                    ) + 5;
                _annotations[key] = {
                    yScaleID: "y1",
                    type: "line",
                    yMin: horizontalLine.y_value,
                    yMax: horizontalLine.y_value,
                    xMax(ctx) {
                        const xScale = ctx.chart.scales.x;
                        // @ts-ignore
                        const { _endValue, _startValue } = xScale;
                        const range = _endValue - _startValue;
                        const xWidth = xScale.width;
                        const pixelsPerUnit = xWidth / range;
                        // end the label before the edge of the graph and consider label width
                        return _endValue - labelWidth / pixelsPerUnit;
                    },
                    borderWidth: 1,
                    label: {
                        display: true,
                        content: horizontalLine.label,
                        position: "end",
                        backgroundColor: "transparent",
                        xAdjust() {
                            const shiftInPx = labelWidth / 2;
                            return shiftInPx;
                        },
                        font: {
                            size: additionalLabelFontSize,
                            weight: "bold" as FontWeight,
                        },
                        color: "black",
                        padding: 0,
                    },
                };
            }
        });
        return _annotations;
    }, [
        graphData.peak1_predicted,
        graphData.peak1_predicted_high,
        graphData.peak1_predicted_label,
        graphData.peak1_predicted_low,
        graphData.x,
        hasSecondGraph,
        showGraph1PointPopup,
        size,
    ]);

    const graph2Annotations = useMemo(
        (): Annotation =>
            hasSecondGraph
                ? {
                      pred_max2_line1: graphData.peak2_predicted
                          ? {
                                yScaleID: "y2",
                                type: "line",
                                display: !showGraph2PointPopup,
                                yMin: graphData.peak2_predicted,
                                yMax: graphData.peak2_predicted,
                                xMax: graphData
                                    .peak2_predicted_label_start_x_value?.[
                                    labelXValueSize
                                ],
                                borderDash: [2.5],
                                borderColor: "black",
                                borderWidth: 1,
                                drawTime: "beforeDraw",
                            }
                          : undefined,
                      pred_max2_line2: graphData.peak2_predicted
                          ? {
                                yScaleID: "y2",
                                type: "line",
                                display: !showGraph2PointPopup,
                                yMin: graphData.peak2_predicted,
                                yMax: graphData.peak2_predicted,
                                xMin: graphData
                                    .peak2_predicted_label_end_x_value?.[
                                    labelXValueSize
                                ],
                                borderDash: [2.5],
                                borderColor: "black",
                                borderWidth: 1,
                            }
                          : undefined,
                      pred_max_box2: {
                          display: showGraph2PointPopup,
                          type: "box",
                          yMin: graphData.peak2_predicted_low,
                          yMax: graphData.peak2_predicted_high,
                          xMin: graphData.x?.[1],
                          yScaleID: "y2",
                          backgroundColor: "#EFEFEF",
                          borderWidth: 0,
                          drawTime: "beforeDraw",
                          z: 5,
                      },
                      pred_max_box_upper_border2: {
                          display: showGraph2PointPopup,
                          type: "line",
                          yMin: graphData.peak2_predicted_high,
                          yMax: graphData.peak2_predicted_high,
                          xMin: graphData.x?.[1],
                          yScaleID: "y2",
                          borderWidth: 1,
                          borderDash: [2],
                          z: 6,
                      },
                      pred_max_box_lower_border2: {
                          display: showGraph2PointPopup,
                          type: "line",
                          yMin: graphData.peak2_predicted_low,
                          yMax: graphData.peak2_predicted_low,
                          xMin: graphData.x?.[1],
                          yScaleID: "y2",
                          borderWidth: 1,
                          borderDash: [2],
                          z: 6,
                      },
                  }
                : {},
        [
            graphData.peak2_predicted,
            graphData.peak2_predicted_high,
            graphData.peak2_predicted_label,
            graphData.peak2_predicted_low,
            graphData.x,
            hasSecondGraph,
            showGraph2PointPopup,
            size,
        ]
    );

    const graph3Annotations = useMemo(
        (): Annotation =>
            hasThirdGraph
                ? {
                      pred_max3_line1: graphData.peak3_predicted
                          ? {
                                yScaleID: "y3",
                                type: "line",
                                display: !showGraph3PointPopup,
                                yMin: graphData.peak3_predicted,
                                yMax: graphData.peak3_predicted,
                                xMax: graphData
                                    .peak3_predicted_label_start_x_value?.[
                                    labelXValueSize
                                ],
                                borderDash: [2.5],
                                borderColor: "black",
                                borderWidth: 1,
                                drawTime: "beforeDraw",
                            }
                          : undefined,
                      pred_max3_line2: graphData.peak3_predicted
                          ? {
                                yScaleID: "y3",
                                type: "line",
                                display: !showGraph3PointPopup,
                                yMin: graphData.peak3_predicted,
                                yMax: graphData.peak3_predicted,
                                xMin: graphData
                                    .peak3_predicted_label_end_x_value?.[
                                    labelXValueSize
                                ],
                                borderDash: [2.5],
                                borderColor: "black",
                                borderWidth: 1,
                            }
                          : undefined,
                      pred_max_box3: {
                          display: showGraph3PointPopup,
                          type: "box",
                          yMin: graphData.peak3_predicted_low,
                          yMax: graphData.peak3_predicted_high,
                          xMin: graphData.x?.[1],
                          yScaleID: "y3",
                          backgroundColor: "#EFEFEF",
                          borderWidth: 0,
                          drawTime: "beforeDraw",
                          z: 5,
                      },
                      pred_max_box_upper_border3: {
                          display: showGraph3PointPopup,
                          type: "line",
                          yMin: graphData.peak3_predicted_high,
                          yMax: graphData.peak3_predicted_high,
                          xMin: graphData.x?.[1],
                          yScaleID: "y3",
                          borderWidth: 1,
                          borderDash: [2],
                          z: 6,
                      },
                      pred_max_box_lower_border3: {
                          display: showGraph3PointPopup,
                          type: "line",
                          yMin: graphData.peak3_predicted_low,
                          yMax: graphData.peak3_predicted_low,
                          xMin: graphData.x?.[1],
                          yScaleID: "y3",
                          borderWidth: 1,
                          borderDash: [2],
                          z: 6,
                      },
                  }
                : {},
        [
            graphData.peak2_predicted,
            graphData.peak2_predicted_high,
            graphData.peak2_predicted_label,
            graphData.peak2_predicted_low,
            graphData.x,
            hasSecondGraph,
            showGraph2PointPopup,
            size,
        ]
    );

    const data = useMemo<
        ChartData<"scatter", (number | Point | null)[], unknown>
    >(
        () => ({
            datasets: [...lineGraphs, ...areaGraphs],
        }),
        [lineGraphs, areaGraphs]
    );

    type Options = ChartOptions<"scatter">;
    type Annotation =
        | _DeepPartialArray<AnnotationOptions<keyof AnnotationTypeRegistry>>
        | _DeepPartialObject<
              Record<string, AnnotationOptions<keyof AnnotationTypeRegistry>>
          >
        | undefined;
    // MARK: Options
    const options: Options = useMemo(() => {
        const aspectRatio =
            size === "compare-secondary"
                ? 2.5
                : isAnaerobic
                ? 1.6
                : isWasserman9
                ? 1.7
                : 1.55;

        const getFontConfig = (
            size: keyof typeof graphConfig.size,
            type: ConfigKey | ConfigKey[]
        ):
            | {}
            | {
                  size: number;
                  weight: FontWeight;
              } => {
            const configTypeArray = Array.isArray(type) ? type : [type];
            const sizeConfig =
                graphConfig.size[size as keyof typeof graphConfig.size];
            if (!sizeConfig) return {};

            const firstExistConfigType = configTypeArray.find(
                (t) => t in sizeConfig
            );
            if (!firstExistConfigType) return {};

            const configItem = sizeConfig[firstExistConfigType]!;
            return {
                ...("fontSize" in configItem
                    ? { size: configItem.fontSize }
                    : {}),
                ...("weight" in configItem
                    ? { weight: configItem.weight }
                    : {}),
            };
        };
        const isBigGraph =
            size === "compare-primary" ||
            size === "compare-secondary" ||
            size === "xxl";
        const hasYLabelUnits =
            "response1_y_label_units" in graphData ||
            "response2_y_label_units" in graphData;

        const additionalVerticalAnnotationKeys = Object.keys(graphData).filter(
            (key) => key.match(/^vertical(\d+)$/)
        ) as Array<`vertical${number}`>;
        const additionalVerticalAnnotations: Annotation = {};
        additionalVerticalAnnotationKeys.forEach((key) => {
            const verticalLine = graphData[key];
            if (verticalLine) {
                additionalVerticalAnnotations[key] = {
                    type: "line",
                    borderWidth: 1,
                    xMin: verticalLine.x_value,
                    xMax: verticalLine.x_value,
                    label: {
                        content: verticalLine.label,
                        font: {
                            weight: "bold",
                            size: additionalLabelFontSize,
                        },
                        display: true,
                        position: "100%",
                        backgroundColor: "white",
                        padding: 0,
                        color: "black",
                        z: 0,
                    },
                };
            }
        });
        const _options: Options = {
            layout: {
                padding: {
                    // make space for the response1_y_label_units
                    top: hasYLabelUnits
                        ? isBigGraph
                            ? 25
                            : size === "md"
                            ? 16
                            : 14
                        : 0,
                    left: hasYLabelUnits
                        ? isBigGraph
                            ? 16
                            : size === "md"
                            ? 8
                            : 6
                        : 0,
                },
            },
            aspectRatio: aspectRatio,
            showLine: true,
            responsive: true,
            animation: false,
            onClick: (e: any, elements: any, chart: Chart) =>
                onGraphClick(e, elements, data.datasets),
            plugins: {
                title: {
                    display: graphConfig.size[size].chart.label,
                    text: graphData.title,
                    color: "#333333",
                    font: {
                        size: 36,
                        weight: "bold",
                    },
                },
                legend: {
                    display: false,
                },
                annotation: {
                    annotations: {
                        predMaxLabel1: graphData.peak1_predicted
                            ? {
                                  type: "label",
                                  yScaleID: "y1",
                                  display: !showGraph1PointPopup,
                                  xValue: graphData
                                      .peak1_predicted_label_start_x_value?.[
                                      labelXValueSize
                                  ],
                                  position: {
                                      x: "start",
                                  },
                                  yValue: graphData.peak1_predicted,
                                  color: "black",
                                  padding: 0,
                                  content:
                                      graphData.peak1_predicted_label ??
                                      "pred. max",
                                  font: {
                                      size: graphConfig.size[size].texts
                                          .fontSize,
                                      weight: "bold",
                                  },
                              }
                            : undefined,
                        predMaxLabel2: graphData.peak2_predicted
                            ? {
                                  type: "label",
                                  yScaleID: "y2",
                                  display: !showGraph2PointPopup,
                                  xValue: graphData
                                      .peak2_predicted_label_start_x_value?.[
                                      labelXValueSize
                                  ],
                                  position: {
                                      x: "start",
                                  },
                                  yValue: graphData.peak2_predicted,
                                  color: "black",
                                  padding: 0,
                                  content:
                                      graphData.peak2_predicted_label ??
                                      "pred. max",
                                  font: {
                                      size: graphConfig.size[size].texts
                                          .fontSize,
                                      weight: "bold",
                                  },
                              }
                            : undefined,
                        predMaxLabel3: graphData.peak3_predicted
                            ? {
                                  type: "label",
                                  yScaleID: "y3",
                                  display: !showGraph3PointPopup,
                                  xValue: graphData
                                      .peak3_predicted_label_start_x_value?.[
                                      labelXValueSize
                                  ],
                                  position: {
                                      x: "start",
                                  },
                                  yValue: graphData.peak3_predicted,
                                  color: "black",
                                  padding: 0,
                                  content:
                                      graphData.peak3_predicted_label ??
                                      "pred. max",
                                  font: {
                                      size: graphConfig.size[size].texts
                                          .fontSize,
                                      weight: "bold",
                                  },
                              }
                            : undefined,
                        slope:
                            graphData.display_slope_value_label !== false &&
                            (graphData.slope_value ||
                                graphData.slope_value === 0)
                                ? {
                                      type: "label",
                                      yScaleID: "y1",
                                      // 7 ticks to left of the max x value
                                      xValue: graphData
                                          .slope_value_label_position?.x,
                                      yValue: graphData
                                          .slope_value_label_position?.y,
                                      backgroundColor: "white",
                                      color: "black",
                                      padding: 0,
                                      content: [
                                          `Slope ${graphData.slope_value.toFixed(
                                              2
                                          )}`,
                                      ],
                                      font: {
                                          weight: "lighter",
                                          // weight: graphConfig.size[size].texts.weight,
                                          size: graphConfig.size[size].texts
                                              .fontSize,
                                      },
                                      z: 10,
                                  }
                                : undefined,
                        lowerLimit: lowerLimit
                            ? {
                                  type: "line",
                                  xMin: lowerLimit,
                                  //   xMin:
                                  //       isAnaerobic || isWasserman9
                                  //           ? graphData.x.findIndex(
                                  //                 (xVal) => xVal === lowerLimit
                                  //             )
                                  //           : lowerLimit,
                                  xMax: lowerLimit,
                                  borderColor: "black",
                                  borderDash: [size === "xxl" ? 10 : 5],
                                  borderWidth: size === "xxl" ? 2.5 : 1,
                                  label: {
                                      content: "LL",
                                      font: {
                                          weight: graphConfig.size[size].limits
                                              .weight,
                                          size: graphConfig.size[size].limits
                                              .fontSize,
                                      },
                                      display: true,
                                      position: "100%",
                                      backgroundColor: "white",
                                      padding: 0,
                                      color: "black",
                                      z: 0,
                                  },
                              }
                            : undefined,
                        upperLimit: upperLimit
                            ? {
                                  type: "line",
                                  xMin: upperLimit,
                                  xMax: upperLimit,
                                  borderColor: "black",
                                  borderDash: [size === "xxl" ? 10 : 5],
                                  borderWidth: size === "xxl" ? 2.5 : 1,
                                  label: {
                                      content: "UL",
                                      font: {
                                          weight: graphConfig.size[size].limits
                                              .weight,
                                          size: graphConfig.size[size].limits
                                              .fontSize,
                                      },
                                      display: true,
                                      position: "100%",
                                      backgroundColor: "white",
                                      padding: 0,
                                      color: "black",
                                      z: 0,
                                  },
                              }
                            : undefined,
                        ...graph1Annotations,
                        ...graph2Annotations,
                        ...graph3Annotations,
                        vat: vat
                            ? {
                                  type: "line",
                                  xMin: graphData.x?.[vat],
                                  xMax: graphData.x?.[vat],
                                  //   xMax: vat,
                                  //   xMin: graphData.x.findIndex(
                                  //       (xVal) => xVal === vat
                                  //   ),
                                  //   xMax: graphData.x.findIndex(
                                  //       (xVal) => xVal === vat
                                  //   ),
                                  //   yMin: Math.min(
                                  //       ...data.datasets.map((dataset) =>
                                  //           dataset.data
                                  //               ? Math.min(...dataset.data)
                                  //               : Infinity
                                  //       )
                                  //   ),
                                  //   yMax: Math.max(
                                  //       ...data.datasets.map((dataset) =>
                                  //           dataset.data
                                  //               ? Math.max(...dataset.data)
                                  //               : -Infinity
                                  //       )
                                  //   ),
                                  borderColor:
                                      isRCP && !isReverseVATColor
                                          ? "black"
                                          : "#D2FC2B",
                                  //   borderColor: "#D2FC2B4D",
                                  //   borderDash: isRCP
                                  //       ? [size === "xxl" ? 10 : 5]
                                  //       : [],
                                  borderWidth: 2,
                                  label: {
                                      content: "VAT",
                                      font: {
                                          weight: graphConfig.size[size].limits
                                              .weight,
                                          size: graphConfig.size[size].limits
                                              .fontSize,
                                      },
                                      display: true,
                                      position: "100%",
                                      backgroundColor: "white",
                                      padding: 0,
                                      color: "black",
                                      z: 0,
                                  },
                              }
                            : undefined,
                        rcp:
                            rcp && isRCP
                                ? {
                                      type: "line",
                                      xMin: graphData.x?.[rcp],
                                      xMax: graphData.x?.[rcp],
                                      borderColor: isReverseVATColor
                                          ? "black"
                                          : "#6cf542",
                                      borderWidth: 2,
                                      label: {
                                          content: "RCP",
                                          font: {
                                              weight: graphConfig.size[size]
                                                  .limits.weight,
                                              size: graphConfig.size[size]
                                                  .limits.fontSize,
                                          },
                                          display: true,
                                          position: "100%",
                                          backgroundColor: "white",
                                          padding: 0,
                                          color: "black",
                                          z: 0,
                                      },
                                  }
                                : undefined,
                        ...additionalVerticalAnnotations,
                    },
                },
            },
            scales: {
                x: {
                    stacked: true,
                    offset: true,
                    min: graphData.x_ticks?.min,
                    max: graphData.x_ticks?.max,
                    title: {
                        display: true,
                        text: graphData.x_label,
                        color: "#333333",
                        font: getFontConfig(size, "axis"),
                        padding: 0,
                    },
                    grid: {
                        display: false, // Remove grid lines on the left Y-axis
                    },
                    border: {
                        color: "#666666",
                        width: 1,
                    },
                    ticks: {
                        padding: 0,
                        font: getFontConfig(size, ["ticks", "texts"]),
                        stepSize: graphData.x_ticks?.step,
                        callback: function (value) {
                            return (+value).toFixed(
                                graphData.x_ticks?.precision
                            );
                        },
                    },
                },
                y3: {
                    offset: true,
                    display: hasThirdGraph,
                    position: "left",
                    max: graphData.response3_y_ticks?.max,
                    min: graphData.response3_y_ticks?.min,
                    border: {
                        width: 0,
                        z: 0,
                    },
                    title: {
                        display: true,
                        padding: { top: -42, bottom: 0 },
                    },
                    ticks: {
                        mirror: true,
                        stepSize: graphData.response3_y_ticks?.step,
                        count: graphData.response3_y_ticks?.count,
                        precision: graphData.response3_y_ticks?.precision,
                        color: graphData.response3_label_color,
                        padding: 10,
                        font: getFontConfig(size, ["ticks", "texts"]),

                        callback: function (value) {
                            return (+value).toFixed(
                                graphData.response3_y_ticks?.precision
                            );
                        },
                    },
                    grid: {
                        display: false,
                    },
                },
                y1: {
                    display: true,
                    position: "left",
                    offset: true,
                    border: {
                        color: "#666666",
                        width: 1,
                        z: 20,
                    },
                    max: graphData.response1_y_ticks?.max,
                    min: graphData.response1_y_ticks?.min,
                    ticks: {
                        stepSize: graphData.response1_y_ticks?.step,
                        count: graphData.response1_y_ticks?.count,
                        precision: graphData.response1_y_ticks?.precision,
                        padding: 0,
                        font: getFontConfig(size, ["ticks", "texts"]),

                        callback: function (value) {
                            return (+value).toFixed(
                                graphData.response1_y_ticks?.precision
                            );
                        },
                    },
                    grid: {
                        display: false,
                    },
                },
                y2: {
                    display: hasSecondGraph,
                    position: "right",
                    offset: true,
                    max: graphData.response2_y_ticks?.max,
                    min: graphData.response2_y_ticks?.min,
                    border: {
                        color: "#666666",
                        width: 1,
                        z: 20,
                    },
                    ticks: {
                        stepSize: graphData.response2_y_ticks?.step,
                        count: graphData.response2_y_ticks?.count,
                        precision: graphData.response2_y_ticks?.precision,
                        color: "#666666",
                        padding: 0,
                        font: getFontConfig(size, ["ticks", "texts"]),

                        callback: function (value) {
                            return (+value).toFixed(
                                graphData.response2_y_ticks?.precision
                            );
                        },
                    },
                    grid: {
                        display: false,
                    },
                },
            },
            elements: {
                line: {
                    tension: 0.1, // bezier curves
                },
            },
        };
        return _options;
    }, [
        size,
        graphData.title,
        graphData.slope_value,
        graphData.slope_value_label_position?.x,
        graphData.slope_value_label_position?.y,
        graphData.x,
        graphData.x_ticks?.min,
        graphData.x_ticks?.max,
        graphData.x_ticks?.step,
        graphData.x_ticks?.count,
        graphData.x_ticks?.precision,
        graphData.x_label,
        graphData.response1_xy,
        graphData.response1_y_ticks?.max,
        graphData.response1_y_ticks?.min,
        graphData.response1_y_ticks?.step,
        graphData.response1_y_ticks?.count,
        graphData.response1_y_ticks?.precision,
        graphData.response1_y_label,
        graphData.response2_y_ticks?.max,
        graphData.response2_y_ticks?.min,
        graphData.response2_y_ticks?.step,
        graphData.response2_y_ticks?.count,
        graphData.response2_y_ticks?.precision,
        graphData.response2_xy,
        graphData.response2_y_label,
        graphData?.response3_y_ticks?.max,
        graphData?.response3_y_ticks?.min,
        graphData?.response3_y_ticks?.step,
        graphData?.response3_y_ticks?.count,
        graphData?.response3_y_ticks?.precision,
        graphData?.response3_xy,
        graphData?.response3_y_label,
        lowerLimit,
        upperLimit,
        graph1Annotations,
        graph2Annotations,
        vat,
        isRCP,
        rcp,
        onGraphClick,
        peak1PointIndex,
        peak2PointIndex,
    ]);

    return { data, options, isRCP, peak1PointIndex, peak2PointIndex };
}
